import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, XCircle, Backpack, Box } from 'lucide-react';
import { useAcademyStore } from '../../../store/academyStore';
import { LessonWrapper, LessonPage } from '../../ui/LessonWrapper';

// ============ LESSON CONTENT ============
const LESSONS: LessonPage[] = [
    {
        id: 1,
        title: "What Are Variables?",
        icon: "📦",
        content: (
            <div className="space-y-6">
                <div className="bg-emerald-50 p-6 rounded-2xl border-2 border-emerald-200">
                    <h3 className="font-bold text-xl text-emerald-800 mb-3">Labeled Boxes for Data</h3>
                    <p className="text-slate-700 mb-4">
                        A <strong>variable</strong> is like a labeled box where you can store information.
                        The label is the variable's <em>name</em>, and what's inside is its <em>value</em>.
                    </p>
                    <p className="text-slate-700">
                        Just like you might label a box "Toys" or "Books" to know what's inside,
                        programmers label variables like <code className="bg-emerald-100 px-2 py-0.5 rounded">playerName</code> or <code className="bg-emerald-100 px-2 py-0.5 rounded">score</code>.
                    </p>
                </div>
                <div className="bg-slate-100 p-5 rounded-xl border border-slate-200">
                    <h4 className="font-bold text-slate-800 mb-3">📦 Variable = Label + Value</h4>
                    <div className="font-mono text-sm bg-slate-800 text-white p-4 rounded-lg">
                        <div><span className="text-purple-400">let</span> <span className="text-yellow-400">playerName</span> = <span className="text-green-400">"Alex"</span>;</div>
                        <div><span className="text-purple-400">let</span> <span className="text-yellow-400">score</span> = <span className="text-orange-400">100</span>;</div>
                        <div><span className="text-purple-400">let</span> <span className="text-yellow-400">isWinner</span> = <span className="text-blue-400">true</span>;</div>
                    </div>
                </div>
            </div>
        )
    },
    {
        id: 2,
        title: "Strings (Text)",
        icon: "📝",
        content: (
            <div className="space-y-6">
                <div className="bg-blue-50 p-6 rounded-2xl border-2 border-blue-200">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center text-2xl">📝</div>
                        <div>
                            <h3 className="font-bold text-xl text-blue-800">String (Text)</h3>
                            <p className="text-blue-600">Words, sentences, or any text</p>
                        </div>
                    </div>
                    <p className="text-slate-700 mb-4">
                        A <strong>string</strong> is any text wrapped in quotes. It can be a word, a sentence, or even numbers written as text!
                    </p>
                    <div className="bg-white p-4 rounded-xl border border-blue-200">
                        <h4 className="font-bold text-blue-700 mb-2">Examples:</h4>
                        <div className="font-mono text-sm space-y-1">
                            <div><span className="text-green-600">"Hello World"</span> - a greeting</div>
                            <div><span className="text-green-600">"Gorilla"</span> - a word</div>
                            <div><span className="text-green-600">"123"</span> - numbers as TEXT (not math!)</div>
                            <div><span className="text-green-600">"🦍"</span> - even emojis!</div>
                        </div>
                    </div>
                </div>
                <div className="bg-amber-50 p-4 rounded-xl border border-amber-200">
                    <h4 className="font-bold text-amber-800 mb-2">🔑 Key Rule: Quotes!</h4>
                    <p className="text-amber-700 text-sm">
                        If you see quotes around it, it's a STRING - even <code className="bg-amber-100 px-1 rounded">"123"</code> is text, not a number for math!
                    </p>
                </div>
            </div>
        )
    },
    {
        id: 3,
        title: "Numbers",
        icon: "🔢",
        content: (
            <div className="space-y-6">
                <div className="bg-orange-50 p-6 rounded-2xl border-2 border-orange-200">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 bg-orange-500 rounded-xl flex items-center justify-center text-2xl">🔢</div>
                        <div>
                            <h3 className="font-bold text-xl text-orange-800">Number</h3>
                            <p className="text-orange-600">For math and counting</p>
                        </div>
                    </div>
                    <p className="text-slate-700 mb-4">
                        A <strong>number</strong> is used for math operations. It has NO quotes around it!
                    </p>
                    <div className="bg-white p-4 rounded-xl border border-orange-200">
                        <h4 className="font-bold text-orange-700 mb-2">Examples:</h4>
                        <div className="font-mono text-sm space-y-1">
                            <div><span className="text-orange-600">42</span> - a whole number</div>
                            <div><span className="text-orange-600">3.14</span> - a decimal</div>
                            <div><span className="text-orange-600">-100</span> - negative numbers</div>
                            <div><span className="text-orange-600">0</span> - zero is a number too!</div>
                        </div>
                    </div>
                </div>
                <div className="bg-red-50 p-4 rounded-xl border border-red-200">
                    <h4 className="font-bold text-red-800 mb-2">⚠️ Common Mistake:</h4>
                    <div className="font-mono text-xs bg-white p-3 rounded border">
                        <div className="text-green-600">"5" + "5" = "55" // String concat!</div>
                        <div className="text-orange-600">5 + 5 = 10 // Number math!</div>
                    </div>
                </div>
            </div>
        )
    },
    {
        id: 4,
        title: "Booleans",
        icon: "✅❌",
        content: (
            <div className="space-y-6">
                <div className="bg-purple-50 p-6 rounded-2xl border-2 border-purple-200">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 bg-purple-500 rounded-xl flex items-center justify-center text-2xl">⚡</div>
                        <div>
                            <h3 className="font-bold text-xl text-purple-800">Boolean</h3>
                            <p className="text-purple-600">True or False - like a light switch</p>
                        </div>
                    </div>
                    <p className="text-slate-700 mb-4">
                        A <strong>boolean</strong> can only be one of two values: <code className="bg-purple-100 px-1 rounded">true</code> or <code className="bg-purple-100 px-1 rounded">false</code>. That's it!
                    </p>
                    <div className="bg-white p-4 rounded-xl border border-purple-200">
                        <h4 className="font-bold text-purple-700 mb-2">Examples:</h4>
                        <div className="font-mono text-sm space-y-1">
                            <div><span className="text-purple-600">true</span> - yes, on, correct</div>
                            <div><span className="text-purple-600">false</span> - no, off, wrong</div>
                        </div>
                    </div>
                </div>
                <div className="bg-slate-100 p-4 rounded-xl border border-slate-200">
                    <h4 className="font-bold text-slate-800 mb-2">🎮 Real-Life Uses:</h4>
                    <ul className="text-slate-600 text-sm space-y-1">
                        <li>• isLoggedIn = true (user is signed in)</li>
                        <li>• hasWon = false (game not won yet)</li>
                        <li>• isDarkMode = true (dark theme on)</li>
                    </ul>
                </div>
            </div>
        )
    },
    {
        id: 5,
        title: "Pro Tips",
        icon: "💡",
        content: (
            <div className="space-y-6">
                <p className="text-slate-600 text-lg mb-4">Master these tips to become a variable pro:</p>
                <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-emerald-50 p-5 rounded-xl border-2 border-emerald-200">
                        <h3 className="font-bold text-emerald-700 mb-2">🏷️ Naming Variables</h3>
                        <p className="text-slate-600 text-sm">
                            Use descriptive names like <code className="bg-emerald-100 px-1 rounded">playerScore</code> instead of <code className="bg-red-100 px-1 rounded">x</code>.
                            Your future self will thank you!
                        </p>
                    </div>
                    <div className="bg-emerald-50 p-5 rounded-xl border-2 border-emerald-200">
                        <h3 className="font-bold text-emerald-700 mb-2">🐪 camelCase</h3>
                        <p className="text-slate-600 text-sm">
                            Start with lowercase, capitalize each new word: <code className="bg-emerald-100 px-1 rounded">myFirstVariable</code>
                        </p>
                    </div>
                    <div className="bg-emerald-50 p-5 rounded-xl border-2 border-emerald-200">
                        <h3 className="font-bold text-emerald-700 mb-2">🔄 let vs const</h3>
                        <p className="text-slate-600 text-sm">
                            Use <code className="bg-emerald-100 px-1 rounded">const</code> for values that won't change,
                            <code className="bg-emerald-100 px-1 rounded">let</code> for values that will.
                        </p>
                    </div>
                    <div className="bg-red-50 p-5 rounded-xl border-2 border-red-200">
                        <h3 className="font-bold text-red-700 mb-2">❌ Forgetting Quotes</h3>
                        <p className="text-slate-600 text-sm">
                            <code className="bg-red-100 px-1 rounded">let name = Alex</code> = ERROR!<br />
                            <code className="bg-green-100 px-1 rounded">let name = "Alex"</code> = Correct!
                        </p>
                    </div>
                </div>
                <div className="bg-slate-800 text-white p-4 rounded-xl mt-4">
                    <h4 className="font-bold text-emerald-300 mb-2">🎮 Ready to Sort?</h4>
                    <p className="text-slate-300 text-sm">
                        In the activity, help the Gorilla pack his backpack by sorting items into the correct Type Pockets!
                    </p>
                </div>
            </div>
        )
    }
];

// ============ DROP ZONE COMPONENT ============
const DropZone = ({ type, color, label, onDrop, items = [], isActive }: any) => {
    return (
        <motion.div
            className={`w-full h-56 rounded-3xl border-4 border-dashed transition-all duration-300 relative overflow-hidden group cursor-pointer
                ${isActive ? 'border-white scale-105 shadow-[0_0_40px_rgba(255,255,255,0.4)]' : 'border-white/20 hover:border-white/40 hover:-translate-y-1'}
                bg-gradient-to-b ${color} backdrop-blur-sm`}
            onClick={onDrop}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
        >
            <div className="absolute inset-0 bg-black/20 group-hover:bg-transparent transition-colors duration-500" />

            <div className="relative z-10 flex flex-col items-center pt-6 h-full p-4">
                <span className="text-white font-black text-3xl drop-shadow-md uppercase tracking-wider">{label}</span>
                <div className="text-white/70 text-xs font-mono mb-4 px-3 py-1 rounded-full bg-black/30 backdrop-blur-md mt-2 border border-white/10">{type}</div>

                <div className="flex flex-wrap gap-2 justify-center w-full overflow-y-auto max-h-[100px] scrollbar-none content-start">
                    <AnimatePresence>
                        {Array.isArray(items) && items.map((item: any, idx: number) => (
                            <motion.div
                                initial={{ scale: 0, rotate: -180 }}
                                animate={{ scale: 1, rotate: 0 }}
                                exit={{ scale: 0 }}
                                key={item.id || idx}
                                className="bg-white/90 text-slate-900 px-3 py-1.5 rounded-lg text-sm font-bold shadow-lg border border-white/50"
                            >
                                {item.value}
                            </motion.div>
                        ))}
                    </AnimatePresence>
                    {(!items || items.length === 0) && (
                        <div className="text-white/30 text-sm font-bold mt-4 flex items-center gap-2">
                            <Box size={16} /> Empty
                        </div>
                    )}
                </div>

                {isActive && (
                    <div className="absolute inset-0 border-4 border-white rounded-3xl animate-pulse" />
                )}
            </div>
        </motion.div>
    );
};

// ============ GAME COMPONENT ============
const VariableGame = ({ onComplete }: { onComplete: () => void }) => {
    const [queue, setQueue] = useState([
        { id: 1, type: 'string', value: '"Hello"' },
        { id: 2, type: 'number', value: '42' },
        { id: 3, type: 'boolean', value: 'true' },
        { id: 4, type: 'string', value: '"Gorilla"' },
        { id: 5, type: 'number', value: '100' },
        { id: 6, type: 'string', value: '"123"' },
        { id: 7, type: 'boolean', value: 'false' },
        { id: 8, type: 'number', value: '3.14' },
        { id: 9, type: 'string', value: '"false"' }, // Tricky one!
        { id: 10, type: 'boolean', value: 'true' }, // Finish strong
    ]);

    const [pockets, setPockets] = useState<{ string: any[], number: any[], boolean: any[] }>({
        string: [],
        number: [],
        boolean: []
    });

    const [feedback, setFeedback] = useState<{ success: boolean, msg: string } | null>(null);
    const [hasCalledComplete, setHasCalledComplete] = useState(false);

    const sortItem = (item: any, targetType: 'string' | 'number' | 'boolean') => {
        if (item.type === targetType) {
            setPockets(prev => ({ ...prev, [targetType]: [...prev[targetType], item] }));
            setQueue(prev => prev.filter(i => i.id !== item.id));
            setFeedback({ success: true, msg: "Great job!" });
        } else {
            setFeedback({ success: false, msg: `Oops! ${item.value} is a ${item.type.toUpperCase()}, not a ${targetType.toUpperCase()}!` });
        }
        setTimeout(() => setFeedback(null), 1500);
    };

    const currentItem = queue[0];

    useEffect(() => {
        if (!currentItem && queue.length === 0 && !hasCalledComplete) {
            setHasCalledComplete(true);
            onComplete();
        }
    }, [queue.length, currentItem, onComplete, hasCalledComplete]);

    return (
        <div className="bg-gradient-to-br from-green-950 via-teal-900 to-slate-900 rounded-3xl p-8 text-white">
            <div className="text-center mb-8">
                <h2 className="text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-teal-300 mb-2">
                    Variables Backpack 🎒
                </h2>
                <p className="text-slate-300">
                    Sort items into the correct Type Pockets!
                </p>
            </div>

            {/* The Queue Area */}
            <div className="h-48 flex flex-col items-center justify-center w-full relative mb-8">
                <AnimatePresence mode='wait'>
                    {currentItem ? (
                        <motion.div
                            key={currentItem.id}
                            initial={{ y: -50, opacity: 0, scale: 0.8 }}
                            animate={{ y: 0, opacity: 1, scale: 1 }}
                            exit={{ y: 50, opacity: 0, scale: 0.8 }}
                            className="flex flex-col items-center gap-4 relative z-10"
                        >
                            <div className="relative">
                                <div className="absolute inset-0 bg-gradient-to-tr from-white/20 to-transparent rounded-3xl pointer-events-none" />
                                <div className="bg-white text-slate-900 px-12 py-8 rounded-3xl shadow-2xl border-b-[8px] border-slate-300 font-mono font-black text-5xl min-w-[240px] text-center transform -rotate-1 relative overflow-hidden">
                                    {currentItem.value}
                                </div>
                            </div>
                            <div className="bg-slate-900/80 px-4 py-1.5 rounded-full text-emerald-400 font-bold text-xs uppercase tracking-widest backdrop-blur-sm border border-white/20 shadow-lg">
                                Click the correct pocket!
                            </div>
                        </motion.div>
                    ) : (
                        <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white px-12 py-6 rounded-3xl font-black text-3xl flex items-center gap-4 shadow-2xl shadow-emerald-900/50 border border-white/20"
                        >
                            <CheckCircle size={40} className="text-emerald-200" /> All Packed!
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>

            {/* Feedback Toast */}
            <AnimatePresence>
                {feedback && (
                    <motion.div
                        initial={{ y: -50, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: -50, opacity: 0 }}
                        className={`text-center px-8 py-3 rounded-2xl font-bold shadow-2xl mb-4 text-lg flex items-center justify-center gap-3 border ${feedback.success
                            ? 'bg-green-500/90 text-white border-green-400'
                            : 'bg-red-500/90 text-white border-red-400'
                            }`}
                    >
                        {feedback.success ? <CheckCircle size={24} /> : <XCircle size={24} />}
                        {feedback.msg}
                    </motion.div>
                )}
            </AnimatePresence>

            {/* The Pockets */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                    <DropZone
                        type="Text"
                        label="String"
                        color="from-blue-600/80 to-blue-900/80"
                        items={pockets.string}
                        onDrop={() => currentItem && sortItem(currentItem, 'string')}
                        isActive={false}
                    />
                    <div className="text-center mt-4 text-sm text-blue-300 font-bold bg-blue-900/30 py-2 rounded-xl border border-blue-500/20">"Quotes" mean String!</div>
                </div>

                <div>
                    <DropZone
                        type="Math"
                        label="Number"
                        color="from-orange-500/80 to-orange-800/80"
                        items={pockets.number}
                        onDrop={() => currentItem && sortItem(currentItem, 'number')}
                        isActive={false}
                    />
                    <div className="text-center mt-4 text-sm text-orange-300 font-bold bg-orange-900/30 py-2 rounded-xl border border-orange-500/20">Just math numbers.</div>
                </div>

                <div>
                    <DropZone
                        type="True / False"
                        label="Boolean"
                        color="from-purple-600/80 to-purple-900/80"
                        items={pockets.boolean}
                        onDrop={() => currentItem && sortItem(currentItem, 'boolean')}
                        isActive={false}
                    />
                    <div className="text-center mt-4 text-sm text-purple-300 font-bold bg-purple-900/30 py-2 rounded-xl border border-purple-500/20">Yes or No toggles.</div>
                </div>
            </div>

            {/* Completion */}
            {!currentItem && (
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-12 flex justify-center"
                >
                    <Link to="/" className="group bg-white hover:bg-emerald-50 text-emerald-900 px-10 py-5 rounded-2xl font-black text-xl shadow-2xl hover:shadow-emerald-500/20 transition-all hover:scale-105 flex items-center gap-3">
                        Return to HQ <ArrowRight className="group-hover:translate-x-1 transition-transform" size={24} />
                    </Link>
                </motion.div>
            )}
        </div>
    );
};

// ============ MAIN COMPONENT ============
export const VariableBackpack = () => {
    // Optimized selector to prevent re-renders from unrelated store changes
    const completeModule = useAcademyStore(state => state.completeModule);
    const [completed, setCompleted] = useState(false);

    // Prevent render loops by stabilizing the callback
    const handleComplete = React.useCallback(() => {
        setCompleted(true);
        completeModule('variables-types', 100, 'badge_variables');
    }, [completeModule]);

    return (
        <LessonWrapper
            moduleName="Variables"
            moduleEmoji="🎒"
            moduleColor="green"
            lessons={LESSONS}
            completed={completed}
            activityLabel="Game"
        >
            <VariableGame onComplete={handleComplete} />
        </LessonWrapper>
    );
};

export default VariableBackpack;
